// Original story content (data only; all prose lives in i18n under `s.*`).
// An ORIGINAL cast and plot inspired by the island-battle-royale genre — not a
// retelling of any specific copyrighted novel. Structure → scenes → lines →
// branching choices. The StoryScene renders it; locales translate it.

import type { JKGameMsgKey } from '../i18n'
import type { Emotion } from '../game/JKGame_assets'

export type SpeakerId = 'you' | 'sakura' | 'rin' | 'kaede' | 'hikari' | 'officer' | 'voice'

export interface SpeakerInfo {
  nameKey: JKGameMsgKey
  set?: string // heroine portrait set; absent = no portrait (system / broadcast)
  color: string
}

export const SPEAKERS: Record<SpeakerId, SpeakerInfo> = {
  you: { nameKey: 's.char.you', set: 'fem_9', color: '#49b0ff' },
  sakura: { nameKey: 's.char.sakura', set: 'fem_1', color: '#5ee68a' },
  rin: { nameKey: 's.char.rin', set: 'fem_3', color: '#ff6b6b' },
  kaede: { nameKey: 's.char.kaede', set: 'fem_5', color: '#8bb4ff' },
  hikari: { nameKey: 's.char.hikari', set: 'fem_7', color: '#ffd34d' },
  officer: { nameKey: 's.char.officer', color: '#ff8a8a' },
  voice: { nameKey: 's.char.voice', color: '#8bb4ff' },
}

export interface StoryLine {
  speaker?: SpeakerId // absent = narration (no name, no portrait)
  textKey: JKGameMsgKey
  emotion?: Emotion
}
export interface StoryChoice {
  textKey: JKGameMsgKey
  goto: string
}
export interface StoryScene {
  id: string
  lines: StoryLine[]
  choices?: StoryChoice[]
  next?: string // when no choices; undefined = chapter end
}
export interface StoryChapter {
  id: string
  index: number // 0 = prologue
  titleKey: JKGameMsgKey
  sumKey: JKGameMsgKey
  start?: string // absent = outline only (not yet playable)
  scenes?: Record<string, StoryScene>
  endsWithMatch?: boolean // after the chapter, drop into a battlefield match
}

const prologue: StoryChapter = {
  id: 'prologue',
  index: 0,
  titleKey: 's.prologue.title',
  sumKey: 's.prologue.sum',
  start: 's1',
  scenes: {
    s1: {
      id: 's1',
      lines: [
        { textKey: 's.prologue.s1.1' },
        { textKey: 's.prologue.s1.2' },
        { textKey: 's.prologue.s1.mid' },
        { textKey: 's.prologue.s1.3' },
      ],
      next: 's2',
    },
    s2: {
      id: 's2',
      lines: [
        { textKey: 's.prologue.s2.1' },
        { textKey: 's.prologue.s2.wake' },
        { speaker: 'officer', textKey: 's.prologue.s2.officer' },
        { speaker: 'officer', textKey: 's.prologue.s2.officer2' },
        { textKey: 's.prologue.s2.2' },
        { speaker: 'sakura', emotion: 'surprised', textKey: 's.prologue.s2.sakura' },
        { textKey: 's.prologue.s2.after' },
      ],
      next: 's3',
    },
    s3: {
      id: 's3',
      lines: [
        { speaker: 'officer', textKey: 's.prologue.s3.officer1' },
        { speaker: 'officer', textKey: 's.prologue.s3.officer2' },
        { speaker: 'officer', textKey: 's.prologue.s3.officer3' },
        { textKey: 's.prologue.s3.after' },
      ],
      next: 's4',
    },
    s4: {
      id: 's4',
      lines: [{ textKey: 's.prologue.s4.q' }],
      choices: [
        { textKey: 's.prologue.s4.cA', goto: 's4a' },
        { textKey: 's.prologue.s4.cB', goto: 's4b' },
        { textKey: 's.prologue.s4.cC', goto: 's4c' },
      ],
    },
    s4a: { id: 's4a', lines: [{ textKey: 's.prologue.s4.rA' }, { textKey: 's.prologue.s4.rA.after' }], next: 's5' },
    // cC (#27): a compassion path between cold observation (cA) and defiance (cB) — steady the
    // panicking classmate. Brings s4 to the 3-option depth of every later chapter (matching the
    // s5 deepening #25) and plants the same trust/empathy theme. Two narration lines (player
    // action → the room eases) mirroring s4a's shape; flows on to s5 like s4a/s4b (not terminal).
    s4c: { id: 's4c', lines: [{ textKey: 's.prologue.s4.rC' }, { textKey: 's.prologue.s4.rC.after' }], next: 's5' },
    // rB split (cf. #20-24): the opening quote is the PLAYER's question (narration), the
    // officer's reply moves to its own correctly-attributed line.
    s4b: { id: 's4b', lines: [{ textKey: 's.prologue.s4.rB' }, { speaker: 'officer', textKey: 's.prologue.s4.rB.officer' }, { textKey: 's.prologue.s4.rB.after' }], next: 's5' },
    s5: {
      id: 's5',
      lines: [
        { textKey: 's.prologue.s5.1' },
        { speaker: 'sakura', emotion: 'smile', textKey: 's.prologue.s5.sakura' },
        { textKey: 's.prologue.s5.q' },
      ],
      choices: [
        { textKey: 's.prologue.s5.cA', goto: 's5a' },
        { textKey: 's.prologue.s5.cB', goto: 's5b' },
        { textKey: 's.prologue.s5.cC', goto: 's5c' },
      ],
    },
    s5a: {
      id: 's5a',
      lines: [
        // rA is narration (taking her hand); Sakura's grateful reply gets its own
        // correctly-attributed line — fixes the name-plate-on-narration AND enriches the
        // prologue's first-alliance payoff with her actual voice.
        { textKey: 's.prologue.s5.rA' },
        { speaker: 'sakura', emotion: 'smile', textKey: 's.prologue.s5.rA.sakura' },
        { textKey: 's.prologue.s5.rA.end' },
      ],
    },
    s5b: {
      id: 's5b',
      lines: [
        { textKey: 's.prologue.s5.rB' },
        // #33: a non-companion internal-resolve beat — gives the solo path the same emotional
        // weight as the two ally outcomes (s5a/s5c, 3 lines each) WITHOUT a second voice, so its
        // chosen solitude is preserved. Surfaces the cost the lone-wolf choice was carrying.
        { textKey: 's.prologue.s5.rB.mid' },
        { textKey: 's.prologue.s5.rB.end' },
      ],
    },
    // cC (#25): a guarded-trust middle path between wholehearted alliance (cA) and going
    // solo (cB) — propose a no-binding pact (walk together, anyone may leave anytime). Brings
    // the prologue's first-choice depth in line with every later chapter (3 options) and
    // plants the "trust vs. self-protection" theme ch2 later pays off. rC narration (player
    // sets the terms) → Sakura's own correctly-attributed reply → terminal end beat, mirroring
    // s5a's three-line shape. Terminal (no next) — prologue ends here like s5a/s5b.
    s5c: {
      id: 's5c',
      lines: [
        { textKey: 's.prologue.s5.rC' },
        { speaker: 'sakura', emotion: 'smile', textKey: 's.prologue.s5.rC.sakura' },
        { textKey: 's.prologue.s5.rC.end' },
      ],
    },
  },
}

const chapter1: StoryChapter = {
  id: 'ch1',
  index: 1,
  titleKey: 's.ch1.title',
  sumKey: 's.ch1.sum',
  start: 's1',
  endsWithMatch: true,
  scenes: {
    s1: {
      id: 's1',
      lines: [
        { textKey: 's.ch1.s1.scene' },
        { speaker: 'voice', textKey: 's.ch1.s1.voice' },
        { textKey: 's.ch1.s1.1' },
        { textKey: 's.ch1.s1.mid' },
      ],
      next: 's2',
    },
    s2: {
      id: 's2',
      lines: [
        { textKey: 's.ch1.s2.1' },
        { speaker: 'hikari', emotion: 'sad', textKey: 's.ch1.s2.hikari' },
        { textKey: 's.ch1.s2.q' },
      ],
      choices: [
        { textKey: 's.ch1.s2.cA', goto: 's2a' },
        { textKey: 's.ch1.s2.cB', goto: 's2b' },
        { textKey: 's.ch1.s2.cC', goto: 's2c' },
      ],
    },
    s2a: {
      id: 's2a',
      lines: [
        { textKey: 's.ch1.s2.rA' },
        { speaker: 'hikari', emotion: 'sad', textKey: 's.ch1.s2.rA.hikari' },
        { textKey: 's.ch1.s2.rA.after' },
      ],
      next: 's3',
    },
    s2b: { id: 's2b', lines: [{ textKey: 's.ch1.s2.rB' }, { textKey: 's.ch1.s2.rB.after' }], next: 's3' },
    s2c: { id: 's2c', lines: [{ textKey: 's.ch1.s2.rC' }, { textKey: 's.ch1.s2.rC.after' }], next: 's3' },
    // s3 (#28): Kaede's last line literally asks the player「你，願意賭一把嗎？」— so make it an
    // actual choice instead of flowing past the question. ch1's SECOND decision point (parity
    // with the prologue's two), branching on the series' trust-vs-self-protection motif; all
    // three replies converge to s4 (Rin's threat) so the spine is unchanged.
    s3: {
      id: 's3',
      lines: [
        { textKey: 's.ch1.s3.1' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch1.s3.kaede' },
        { textKey: 's.ch1.s3.2' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch1.s3.kaede2' },
      ],
      choices: [
        { textKey: 's.ch1.s3.cA', goto: 's3a' },
        { textKey: 's.ch1.s3.cB', goto: 's3b' },
        { textKey: 's.ch1.s3.cC', goto: 's3c' },
      ],
    },
    s3a: {
      id: 's3a',
      lines: [
        { textKey: 's.ch1.s3.rA' },
        { speaker: 'kaede', emotion: 'smile', textKey: 's.ch1.s3.rA.kaede' },
      ],
      next: 's4',
    },
    s3b: {
      id: 's3b',
      lines: [
        { textKey: 's.ch1.s3.rB' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch1.s3.rB.kaede' },
      ],
      next: 's4',
    },
    s3c: {
      id: 's3c',
      lines: [
        { textKey: 's.ch1.s3.rC' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch1.s3.rC.kaede' },
      ],
      next: 's4',
    },
    s4: {
      id: 's4',
      lines: [
        { textKey: 's.ch1.s4.1' },
        { speaker: 'rin', emotion: 'angry', textKey: 's.ch1.s4.rin' },
        { textKey: 's.ch1.s4.2' },
        { textKey: 's.ch1.s4.after' },
      ],
      next: 's5',
    },
    s5: {
      id: 's5',
      lines: [
        { textKey: 's.ch1.s5.1' },
        { speaker: 'sakura', emotion: 'angry', textKey: 's.ch1.s5.sakura' },
        { textKey: 's.ch1.s5.2' },
        { textKey: 's.ch1.s5.end' },
      ],
    },
  },
}

const chapter2: StoryChapter = {
  id: 'ch2',
  index: 2,
  titleKey: 's.ch2.title',
  sumKey: 's.ch2.sum',
  start: 's1',
  endsWithMatch: true,
  scenes: {
    s1: { id: 's1', lines: [{ textKey: 's.ch2.s1.scene' }, { speaker: 'voice', textKey: 's.ch2.s1.voice' }, { textKey: 's.ch2.s1.1' }, { textKey: 's.ch2.s1.mid' }, { textKey: 's.ch2.s1.2' }], next: 's2' },
    s2: {
      id: 's2',
      lines: [{ speaker: 'kaede', textKey: 's.ch2.s2.kaede' }, { textKey: 's.ch2.s2.q' }],
      choices: [
        { textKey: 's.ch2.s2.cA', goto: 's2a' },
        { textKey: 's.ch2.s2.cB', goto: 's2b' },
        { textKey: 's.ch2.s2.cC', goto: 's2c' },
      ],
    },
    s2a: {
      id: 's2a',
      lines: [
        { textKey: 's.ch2.s2.rA' },
        { speaker: 'kaede', emotion: 'surprised', textKey: 's.ch2.s2.rA.kaede' },
        { textKey: 's.ch2.s2.rA.after' },
      ],
      next: 's3',
    },
    s2b: {
      id: 's2b',
      lines: [
        { textKey: 's.ch2.s2.rB.before' },
        // Split (cf. #22): the quote「我自己確認過再說」is the PLAYER's and the line narrates
        // Kaede in third person ("楓沒有生氣，只是淡淡地說"), so that stays narration; her own
        // reply moves to its correctly-attributed line below.
        { textKey: 's.ch2.s2.rB' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch2.s2.rB.kaede' },
        { textKey: 's.ch2.s2.rB.after' },
      ],
      next: 's3',
    },
    s2c: {
      id: 's2c',
      lines: [
        { textKey: 's.ch2.s2.rC' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch2.s2.rC.kaede' },
        { textKey: 's.ch2.s2.rC.after' },
      ],
      next: 's3',
    },
    // s3 (#29): the cornered rumor-monger points a gun and pleads「我只是想活下去……難道你們
    // 不是嗎？」— ch2's SECOND choice point (parity with ch1 #28), a moral stance on the
    // chapter's survival-as-excuse theme. All three converge to s4 (the fight follows
    // regardless — the chapter endsWithMatch), so the spine is unchanged; the pick colours
    // the player's character, not the outcome.
    s3: {
      id: 's3',
      lines: [
        { textKey: 's.ch2.s3.1' },
        { textKey: 's.ch2.s3.2' },
        { speaker: 'kaede', emotion: 'angry', textKey: 's.ch2.s3.kaede' },
      ],
      choices: [
        { textKey: 's.ch2.s3.cA', goto: 's3a' },
        { textKey: 's.ch2.s3.cB', goto: 's3b' },
        { textKey: 's.ch2.s3.cC', goto: 's3c' },
      ],
    },
    s3a: {
      id: 's3a',
      lines: [
        { textKey: 's.ch2.s3.rA' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch2.s3.rA.kaede' },
      ],
      next: 's4',
    },
    s3b: {
      id: 's3b',
      lines: [
        { textKey: 's.ch2.s3.rB' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch2.s3.rB.kaede' },
      ],
      next: 's4',
    },
    s3c: {
      id: 's3c',
      lines: [
        { textKey: 's.ch2.s3.rC' },
        { speaker: 'kaede', emotion: 'surprised', textKey: 's.ch2.s3.rC.kaede' },
      ],
      next: 's4',
    },
    s4: {
      id: 's4',
      lines: [
        { textKey: 's.ch2.s4.1' },
        { speaker: 'sakura', emotion: 'angry', textKey: 's.ch2.s4.sakura' },
        { textKey: 's.ch2.s4.end' },
      ],
    },
  },
}

const chapter3: StoryChapter = {
  id: 'ch3',
  index: 3,
  titleKey: 's.ch3.title',
  sumKey: 's.ch3.sum',
  start: 's1',
  endsWithMatch: true,
  scenes: {
    s1: { id: 's1', lines: [{ textKey: 's.ch3.s1.scene' }, { speaker: 'kaede', textKey: 's.ch3.s1.kaede' }, { speaker: 'kaede', emotion: 'plane', textKey: 's.ch3.s1.kaede2' }, { textKey: 's.ch3.s1.1' }, { speaker: 'voice', textKey: 's.ch3.s1.voice' }, { textKey: 's.ch3.s1.mid' }], next: 's2' },
    s2: {
      id: 's2',
      lines: [{ textKey: 's.ch3.s2.q' }],
      choices: [
        { textKey: 's.ch3.s2.cA', goto: 's2a' },
        { textKey: 's.ch3.s2.cB', goto: 's2b' },
        { textKey: 's.ch3.s2.cC', goto: 's2c' },
      ],
    },
    s2a: {
      id: 's2a',
      lines: [
        { textKey: 's.ch3.s2.rA' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch3.s2.rA.kaede' },
      ],
      next: 's3',
    },
    s2b: {
      id: 's2b',
      lines: [
        // Narration, not a Kaede line: the opening quote「跳過校驗、直接灌！」is the PLAYER's
        // command (choice cB), and the line narrates Kaede in the third person ("楓咬牙照做").
        // Attributing it to Kaede mislabels the speaker — and leaving it as narration also
        // matches s2a/s2c, which open with a player-action beat before Kaede reacts.
        { textKey: 's.ch3.s2.rB' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch3.s2.rB.after' },
      ],
      next: 's3',
    },
    s2c: {
      id: 's2c',
      lines: [
        { textKey: 's.ch3.s2.rC' },
        { speaker: 'kaede', emotion: 'smile', textKey: 's.ch3.s2.rC.kaede' },
      ],
      next: 's3',
    },
    s3: {
      id: 's3',
      lines: [
        { speaker: 'kaede', emotion: 'surprised', textKey: 's.ch3.s3.kaede' },
        // The progress-bar fact is a SCENE beat, not Kaede's dialogue (cf. #20-23) — split
        // to narration; Sakura's next line ("別看進度條") references it, so it reads cleanly.
        { textKey: 's.ch3.s3.bar' },
        { speaker: 'sakura', emotion: 'smile', textKey: 's.ch3.s3.sakura' },
        { speaker: 'kaede', emotion: 'blush', textKey: 's.ch3.s3.kaede2' },
        { textKey: 's.ch3.s3.beat' },
        { textKey: 's.ch3.s3.1' },
      ],
      next: 's4',
    },
    // s4 (#30): Kaede asks you to hold the line for the last two minutes — ch3's SECOND choice
    // point (parity with ch1 #28 / ch2 #29): HOW you defend. All three converge to the s5
    // finale (the fight follows — chapter endsWithMatch), so the spine is unchanged; cC echoes
    // Sakura's s3 line「一起進來的，就要一起出去」.
    s4: {
      id: 's4',
      lines: [
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch3.s4.kaede' },
      ],
      choices: [
        { textKey: 's.ch3.s4.cA', goto: 's4a' },
        { textKey: 's.ch3.s4.cB', goto: 's4b' },
        { textKey: 's.ch3.s4.cC', goto: 's4c' },
      ],
    },
    s4a: {
      id: 's4a',
      lines: [
        { textKey: 's.ch3.s4.rA' },
        { speaker: 'kaede', emotion: 'plane', textKey: 's.ch3.s4.rA.kaede' },
      ],
      next: 's5',
    },
    s4b: {
      id: 's4b',
      lines: [
        { textKey: 's.ch3.s4.rB' },
        { speaker: 'kaede', emotion: 'surprised', textKey: 's.ch3.s4.rB.kaede' },
      ],
      next: 's5',
    },
    s4c: {
      id: 's4c',
      lines: [
        { textKey: 's.ch3.s4.rC' },
        { speaker: 'sakura', emotion: 'angry', textKey: 's.ch3.s4.rC.sakura' },
      ],
      next: 's5',
    },
    s5: {
      id: 's5',
      lines: [
        { textKey: 's.ch3.s4.1' },
        { textKey: 's.ch3.s4.end' },
      ],
    },
  },
}

const chapter4: StoryChapter = {
  id: 'ch4',
  index: 4,
  titleKey: 's.ch4.title',
  sumKey: 's.ch4.sum',
  start: 's1',
  endsWithMatch: true,
  scenes: {
    s1: { id: 's1', lines: [{ textKey: 's.ch4.s1.scene' }, { speaker: 'voice', textKey: 's.ch4.s1.voice' }, { textKey: 's.ch4.s1.1' }, { speaker: 'rin', emotion: 'angry', textKey: 's.ch4.s1.rin' }, { textKey: 's.ch4.s1.mid' }], next: 's2' },
    s2: {
      id: 's2',
      lines: [{ textKey: 's.ch4.s2.q' }],
      choices: [
        { textKey: 's.ch4.s2.cA', goto: 's2a' },
        { textKey: 's.ch4.s2.cB', goto: 's2b' },
        { textKey: 's.ch4.s2.cC', goto: 's2c' },
      ],
    },
    s2a: {
      id: 's2a',
      lines: [
        { textKey: 's.ch4.s2.rA' },
        { speaker: 'rin', emotion: 'smile', textKey: 's.ch4.s2.rA.rin' },
        { textKey: 's.ch4.s2.rA.after' },
      ],
      next: 's3',
    },
    s2b: {
      id: 's2b',
      lines: [
        // Narration, not a Rin line (cf. ch3 s2b #20): the quote is the PLAYER's plea
        // (choice cB) and the rest narrates Rin in third person ("凜沉默了半秒"). Rin's
        // own reply follows in rB.rin. Leaving it as narration also matches s2a/s2c.
        { textKey: 's.ch4.s2.rB' },
        { speaker: 'rin', emotion: 'sad', textKey: 's.ch4.s2.rB.rin' },
        { textKey: 's.ch4.s2.rB.after' },
      ],
      next: 's3',
    },
    s2c: {
      id: 's2c',
      lines: [
        { textKey: 's.ch4.s2.rC' },
        { speaker: 'rin', emotion: 'sad', textKey: 's.ch4.s2.rC.rin' },
        { textKey: 's.ch4.s2.rC.after' },
      ],
      next: 's3',
    },
    // s3 (#31): Rin retreats with the telling line「還沒決定要把最後一發留給誰」— ch4's SECOND
    // choice point (parity with ch1 #28 / ch2 #29 / ch3 #30): how you answer the crack in the
    // coldest hunter's armour (reach out / bank the opening / stay wary). All three converge to
    // the s4 finale (existing s3.1/s3.2/s3.end), chapter endsWithMatch — spine unchanged.
    s3: {
      id: 's3',
      lines: [
        { speaker: 'rin', emotion: 'angry', textKey: 's.ch4.s3.rin' },
      ],
      choices: [
        { textKey: 's.ch4.s3.cA', goto: 's3a' },
        { textKey: 's.ch4.s3.cB', goto: 's3b' },
        { textKey: 's.ch4.s3.cC', goto: 's3c' },
      ],
    },
    s3a: {
      id: 's3a',
      lines: [
        { textKey: 's.ch4.s3.rA' },
        { speaker: 'rin', emotion: 'sad', textKey: 's.ch4.s3.rA.rin' },
      ],
      next: 's4',
    },
    s3b: {
      id: 's3b',
      lines: [
        { textKey: 's.ch4.s3.rB' },
        { speaker: 'rin', emotion: 'angry', textKey: 's.ch4.s3.rB.rin' },
      ],
      next: 's4',
    },
    s3c: {
      id: 's3c',
      lines: [
        { textKey: 's.ch4.s3.rC' },
        { speaker: 'rin', emotion: 'angry', textKey: 's.ch4.s3.rC.rin' },
      ],
      next: 's4',
    },
    s4: {
      id: 's4',
      lines: [
        { textKey: 's.ch4.s3.1' },
        { textKey: 's.ch4.s3.2' },
        { textKey: 's.ch4.s3.end' },
      ],
    },
  },
}

const chapter5: StoryChapter = {
  id: 'ch5',
  index: 5,
  titleKey: 's.ch5.title',
  sumKey: 's.ch5.sum',
  start: 's1',
  endsWithMatch: true,
  scenes: {
    s1: { id: 's1', lines: [{ textKey: 's.ch5.s1.scene' }, { speaker: 'voice', textKey: 's.ch5.s1.voice' }, { speaker: 'kaede', emotion: 'sad', textKey: 's.ch5.s1.kaede' }, { textKey: 's.ch5.s1.1' }, { textKey: 's.ch5.s1.mid' }, { speaker: 'hikari', emotion: 'smile', textKey: 's.ch5.s1.hikari' }, { textKey: 's.ch5.s1.after' }], next: 's2' },
    s2: {
      id: 's2',
      lines: [{ textKey: 's.ch5.s2.q' }],
      choices: [
        { textKey: 's.ch5.s2.cA', goto: 's2a' },
        { textKey: 's.ch5.s2.cB', goto: 's2b' },
        { textKey: 's.ch5.s2.cC', goto: 's2c' },
      ],
    },
    s2a: {
      id: 's2a',
      lines: [
        // Narration, not a Sakura line (cf. ch3 s2b #20): the quote「我去。」is the PLAYER's
        // ("你聽見自己這麼說") and the rest narrates Sakura in third person ("さくら想攔"). Her
        // own reply follows in rA.sakura. Narration here also matches s2c's opening beat.
        { textKey: 's.ch5.s2.rA' },
        { speaker: 'sakura', emotion: 'angry', textKey: 's.ch5.s2.rA.sakura' },
        { textKey: 's.ch5.s2.rA.after' },
      ],
      next: 's3',
    },
    s2b: {
      id: 's2b',
      lines: [
        // Split from a former single Kaede line (cf. #20/#21): the opening quote is the
        // PLAYER's and the line narrates Kaede in third person ("楓苦笑"), so that stays
        // narration; her actual reply moves to its own correctly-attributed line below.
        { textKey: 's.ch5.s2.rB' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch5.s2.rB.kaede' },
        { speaker: 'sakura', emotion: 'angry', textKey: 's.ch5.s2.rB.sakura' },
        { textKey: 's.ch5.s2.rB.after' },
      ],
      next: 's3',
    },
    s2c: {
      id: 's2c',
      lines: [
        { textKey: 's.ch5.s2.rC' },
        { speaker: 'kaede', emotion: 'smile', textKey: 's.ch5.s2.rC.kaede' },
        { textKey: 's.ch5.s2.rC.after' },
      ],
      next: 's3',
    },
    // s3 (#32): the farewell before the relay run — Kaede confesses guilt over the coordinates
    // and begs you to come back, Hikari promises to count the time. ch5's SECOND choice point
    // (completing the prologue + ch1–ch5 two-decision arc): your parting resolve. All three
    // converge to the s4 finale (existing s3.end), chapter endsWithMatch — spine unchanged.
    s3: {
      id: 's3',
      lines: [
        { textKey: 's.ch5.s3.1' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch5.s3.kaede' },
        { speaker: 'hikari', emotion: 'sad', textKey: 's.ch5.s3.hikari' },
      ],
      choices: [
        { textKey: 's.ch5.s3.cA', goto: 's3a' },
        { textKey: 's.ch5.s3.cB', goto: 's3b' },
        { textKey: 's.ch5.s3.cC', goto: 's3c' },
      ],
    },
    s3a: {
      id: 's3a',
      lines: [
        { textKey: 's.ch5.s3.rA' },
        { speaker: 'kaede', emotion: 'sad', textKey: 's.ch5.s3.rA.kaede' },
      ],
      next: 's4',
    },
    s3b: {
      id: 's3b',
      lines: [
        { textKey: 's.ch5.s3.rB' },
        { speaker: 'hikari', emotion: 'smile', textKey: 's.ch5.s3.rB.hikari' },
      ],
      next: 's4',
    },
    s3c: {
      id: 's3c',
      lines: [
        { textKey: 's.ch5.s3.rC' },
        { speaker: 'hikari', emotion: 'plane', textKey: 's.ch5.s3.rC.hikari' },
      ],
      next: 's4',
    },
    s4: {
      id: 's4',
      lines: [
        { textKey: 's.ch5.s3.end' },
      ],
    },
  },
}

const chapter6: StoryChapter = {
  id: 'ch6',
  index: 6,
  titleKey: 's.ch6.title',
  sumKey: 's.ch6.sum',
  start: 's1',
  scenes: {
    s1: { id: 's1', lines: [{ textKey: 's.ch6.s1.scene' }, { textKey: 's.ch6.s1.1' }, { speaker: 'voice', textKey: 's.ch6.s1.voice' }, { textKey: 's.ch6.s1.2' }, { textKey: 's.ch6.s1.mid' }], next: 's1b' },
    s1b: {
      id: 's1b',
      lines: [
        { textKey: 's.ch6.s1b.1' },
        { speaker: 'sakura', emotion: 'sad', textKey: 's.ch6.s1b.sakura' },
        { speaker: 'kaede', emotion: 'smile', textKey: 's.ch6.s1b.kaede' },
        { speaker: 'hikari', emotion: 'smile', textKey: 's.ch6.s1b.hikari' },
        { textKey: 's.ch6.s1b.absent' },
        { textKey: 's.ch6.s1b.2' },
      ],
      next: 's2',
    },
    s2: {
      id: 's2',
      lines: [{ textKey: 's.ch6.s2.q' }],
      choices: [
        { textKey: 's.ch6.s2.cA', goto: 'e1' },
        { textKey: 's.ch6.s2.cB', goto: 'e2' },
        { textKey: 's.ch6.s2.cC', goto: 'e3' },
      ],
    },
    e1: { id: 'e1', lines: [{ textKey: 's.ch6.e1.1' }, { textKey: 's.ch6.e1.2' }, { speaker: 'sakura', emotion: 'sad', textKey: 's.ch6.e1.3' }, { textKey: 's.ch6.e1.end' }, { textKey: 's.ch6.complete' }] },
    e2: { id: 'e2', lines: [{ textKey: 's.ch6.e2.1' }, { textKey: 's.ch6.e2.2' }, { speaker: 'kaede', emotion: 'smile', textKey: 's.ch6.e2.3' }, { textKey: 's.ch6.e2.end' }, { textKey: 's.ch6.complete' }] },
    e3: { id: 'e3', lines: [{ textKey: 's.ch6.e3.1' }, { speaker: 'officer', textKey: 's.ch6.e3.officer' }, { textKey: 's.ch6.e3.2' }, { speaker: 'sakura', emotion: 'angry', textKey: 's.ch6.e3.3' }, { textKey: 's.ch6.e3.end' }, { textKey: 's.ch6.complete' }] },
  },
}

export const CHAPTERS: readonly StoryChapter[] = [prologue, chapter1, chapter2, chapter3, chapter4, chapter5, chapter6]

export function chapterById(id: string): StoryChapter | undefined {
  return CHAPTERS.find((c) => c.id === id)
}

/**
 * Validate a chapter's branching graph. Two distinct classes of authoring bug,
 * neither catchable by TS (unlike `textKey`, which is typed as JKGameMsgKey):
 *   1. DANGLING ref — `start` / a scene's `next` / a choice's `goto` points at an
 *      id with no matching scene. At runtime StoryScene.gotoScene() falls through
 *      to endChapter(), silently completing AND unlocking the chapter.
 *   2. ORPHAN scene — a node that exists but is unreachable from `start` (e.g. a
 *      stale node left behind after a `goto` was repointed). Dead content / waste.
 * Returns a list of human-readable error strings (empty = valid). Outline-only
 * chapters (no start/scenes) are skipped. Pure; no I/O.
 */
export function findStoryGraphErrors(chapter: StoryChapter): string[] {
  const errors: string[] = []
  const scenes = chapter.scenes
  if (!chapter.start || !scenes) return errors // outline-only chapter — nothing to wire yet
  const has = (id: string): boolean => Object.prototype.hasOwnProperty.call(scenes, id)
  if (!has(chapter.start)) errors.push(`start → missing scene '${chapter.start}'`)
  for (const [key, sc] of Object.entries(scenes)) {
    if (sc.next !== undefined && !has(sc.next)) errors.push(`${key}.next → missing scene '${sc.next}'`)
    if (sc.choices && sc.next !== undefined) errors.push(`${key} has both choices and next (next is ignored)`)
    for (const c of sc.choices ?? []) {
      if (!has(c.goto)) errors.push(`${key} choice → missing scene '${c.goto}'`)
    }
  }
  // Reachability: walk from `start` following only refs that EXIST (missing ones are already
  // reported above), then flag any scene never reached — an orphan the dangling-ref pass can't
  // catch (its references are all valid; it's simply never pointed AT).
  if (has(chapter.start)) {
    const reached = new Set<string>()
    const stack = [chapter.start]
    while (stack.length > 0) {
      const id = stack.pop() as string
      if (reached.has(id)) continue
      const sc = scenes[id]
      if (!sc) continue
      reached.add(id)
      if (sc.next !== undefined && has(sc.next)) stack.push(sc.next)
      for (const c of sc.choices ?? []) {
        if (has(c.goto)) stack.push(c.goto)
      }
    }
    for (const key of Object.keys(scenes)) {
      if (!reached.has(key)) errors.push(`scene '${key}' is unreachable from start`)
    }
  }
  return errors
}
